import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined'
import FilterIcon from 'assets/FilterIcon'
import { ReactComponent as TranslationCameraIcon } from 'assets/translation_deviations_logo.svg'
import { useUpdateMilkingProcessDeviationFalseMark } from 'hook/mutations/useUpdateMilkingProcessDeviationFalseMark'
import { useEffect, useMemo, useRef } from 'react'

import {
	deviationLabels,
	IMilkingProcessDeviationEntry,
	IMilkingProcessDeviationResponse,
	MilkingProcessDeviationType,
} from 'models/MilkingProcess'
import moment from 'moment'
import { COLORS } from 'styles/colors'
import SelectSmall from 'ui/menu/SelectForm'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import CustomTableRow from 'ui/table/CustomTableRow'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { NoDataPlaceholder } from '../../NoDataPlaceholder'
import { DeviationVideoModal } from './deviation-video-modal'
import {
	applyMilkingOverlayMock,
	getMilkingOverlayMockMode,
} from './deviation-video-modal/helper/milkingOverlayMock'
import {
	DeviationOrderingField,
	DeviationServerOrderingField,
} from './hooks/useDeviationSorting'
import { useDeviationVideoModal } from './hooks/useDeviationVideoModal'

type DeviationSortingState = Record<
	DeviationServerOrderingField | 'rowNumber',
	boolean
>

type DeviationTableProps = {
	deviations?: IMilkingProcessDeviationResponse
	isLoading: boolean
	emptyState?: 'no-data' | 'no-deviations' | null
	page: number
	pageSize: number
	setPage: (page: number) => void
	milkersForFilter: { type: number; value: string }[]
	setSelectedMilkers: (milkers: number[]) => void
	deviationTypesForFilter: {
		type: MilkingProcessDeviationType
		value: string
	}[]
	setSelectedDeviationTypes: (types: MilkingProcessDeviationType[]) => void
	sortingState: DeviationSortingState
	isRowNumberReversed: boolean
	toggleSort: (field: DeviationOrderingField) => void
}

export const DeviationTable = (props: DeviationTableProps) => {
	const {
		isLoading,
		deviations,
		emptyState,
		page,
		pageSize,
		setPage,
		milkersForFilter,
		setSelectedMilkers,
		deviationTypesForFilter,
		setSelectedDeviationTypes,
		sortingState,
		isRowNumberReversed,
		toggleSort,
	} = props
	const containerRef = useRef<HTMLDivElement | null>(null)
	const overlayMockMode = getMilkingOverlayMockMode()
	const deviationsResult = useMemo(
		() => applyMilkingOverlayMock(deviations?.results, overlayMockMode),
		[deviations?.results, overlayMockMode],
	)
	const tableEmptyState =
		overlayMockMode && deviationsResult?.length ? null : emptyState
	const { mutate: updateDeviationFalseMark, isPending: isFalseMarkUpdating } =
		useUpdateMilkingProcessDeviationFalseMark()

	const {
		isVideoOpen,
		videoUrl,
		selectedEntry,
		openModal,
		closeModal,
		canPrev,
		canNext,
		showPrev,
		showNext,
	} = useDeviationVideoModal({
		currentPage: page,
		totalCount: Math.max(deviations?.count || 0, deviationsResult?.length || 0),
		pageSize: pageSize,
		onPageChange: setPage,
		currentPageDeviations: deviationsResult || [],
	})
	const rowOffset = (page - 1) * pageSize
	const handleToggleFalseMark = (entry: IMilkingProcessDeviationEntry) => {
		updateDeviationFalseMark({
			id: entry.id,
			isFalse: !entry.is_false,
		})
	}
	const tableHead = {
		rowKey: 'tableDeviationHeader',
		rowBorderBottom: COLORS.secondaryFontOpacity,
		height: '38px',
		cellItems: [
			{
				verticalAlign: 'center',
				width: '65px',
				children: (
					<Stack direction={'row'} alignItems={'center'} gap={'4px'}>
						<TypographySecondary>№</TypographySecondary>
						<FilterIcon
							color={sortingState.rowNumber ? COLORS.main : COLORS.icon}
							onClick={() => toggleSort('rowNumber')}
						/>
					</Stack>
				),
			},
			{
				width: '110px',
				verticalAlign: 'center',

				children: (
					<Stack
						direction={'row'}
						alignItems={'center'}
						gap={'4px'}
						sx={{ cursor: 'pointer' }}
						onClick={() => toggleSort('timestamp')}
					>
						<TypographySecondary>
							Дата
							<br />и время
						</TypographySecondary>
						<FilterIcon
							color={sortingState.timestamp ? COLORS.main : COLORS.icon}
						/>
					</Stack>
				),
			},
			{
				width: '100px',
				verticalAlign: 'center',

				children: <TypographySecondary>Доильный зал</TypographySecondary>,
			},
			{
				width: '100px',
				verticalAlign: 'center',

				children: <TypographySecondary>Смена</TypographySecondary>,
			},
			{
				width: '60px',
				verticalAlign: 'center',

				children: (
					<Stack
						direction={'row'}
						alignItems={'center'}
						gap={'4px'}
						sx={{ cursor: 'pointer' }}
						onClick={() => toggleSort('milking_unit_num')}
					>
						<TypographySecondary>Место</TypographySecondary>
						<FilterIcon
							color={sortingState.milking_unit_num ? COLORS.main : COLORS.icon}
						/>
					</Stack>
				),
			},
			{
				width: '180px',
				verticalAlign: 'center',

				children: (
					<SelectSmall
						width='150px'
						typeArray={milkersForFilter}
						isCheckbox
						isMultiplaySelect
						state={setSelectedMilkers}
						placeholder={'Дояр'}
						isClearIcon
					/>
				),
			},
			{
				width: '300px',
				verticalAlign: 'center',

				children: (
					<SelectSmall
						width='270px'
						typeArray={deviationTypesForFilter}
						isCheckbox
						isMultiplaySelect
						state={setSelectedDeviationTypes}
						placeholder={'Отклонение'}
						isClearIcon
					/>
				),
			},
			{
				width: '60px',
				verticalAlign: 'center',
				children: (
					<TypographySecondary sx={{ textAlign: 'right' }}>
						Действия
					</TypographySecondary>
				),
			},
		],
	}

	const transformedData = deviationsResult?.map((el, index) => ({
		rowKey: `deviation-row-${index}`,
		rowHeight: '40px',
		rowHover: true,

		cellItems: [
			{
				width: '5%',
				verticalAlign: 'center',
				children: (
					<Stack direction='row' alignItems='center' gap='4px'>
						<TypographyPrimary>{rowOffset + index + 1}</TypographyPrimary>
						{el.is_false && (
							<TooltipHover
								title='Отмечено ложное срабатывание'
								placement='top-start'
								oneLine
								disableHoverBackground
							>
								<Box
									sx={{
										display: 'flex',
										color: COLORS.icon,
										cursor: 'default',
										'&:hover': {
											color: COLORS.main,
										},
									}}
								>
									<ReportGmailerrorredOutlinedIcon sx={{ fontSize: 18 }} />
								</Box>
							</TooltipHover>
						)}
					</Stack>
				),
			},
			{
				width: '15%',
				verticalAlign: 'center',

				children: (
					<TypographyPrimary>
						{moment(el.timestamp).format('DD.MM.YY, HH:mm')}
					</TypographyPrimary>
				),
			},
			{
				width: '15%',
				verticalAlign: 'center',

				children: (
					<TypographyPrimary>{el.milking_hall ?? '-'}</TypographyPrimary>
				),
			},
			{
				width: '10%',
				verticalAlign: 'center',

				children: (
					<TypographyPrimary>
						{
							{
								am: 'Утренняя',
								pm: 'Дневная',
								night: 'Ночная',
							}[el.period]
						}
					</TypographyPrimary>
				),
			},
			{
				width: '10%',
				verticalAlign: 'center',

				children: <TypographyPrimary>{el.milking_unit_num}</TypographyPrimary>,
			},
			{
				width: '15%',
				verticalAlign: 'center',

				children: <TypographyPrimary>{el.milker ?? '-'}</TypographyPrimary>,
			},
			{
				width: '300px',
				verticalAlign: 'center',
				children: (
					<TypographyPrimary>
						{deviationLabels[el.deviation.type]}
						{el.deviation.length_seconds > 0 && (
							<span
								style={{
									marginLeft: '4px',
									color: COLORS.secondary,
									fontSize: 'inherit',
									whiteSpace: 'nowrap',
								}}
							>
								/ {el.deviation.length_seconds} сек
							</span>
						)}
					</TypographyPrimary>
				),
			},
			{
				width: '71px',
				verticalAlign: 'center',
				children: (
					<Stack
						direction={'row'}
						justifyContent={'flex-end'}
						alignItems={'center'}
					>
						{el.video_url && (
							<TooltipHover
								title='Смотреть видеофиксацию отклонения'
								placement='bottom-end'
							>
								<TranslationCameraIcon
									style={{ cursor: 'pointer' }}
									onClick={() => {
										openModal(el, deviationsResult)
									}}
								/>
							</TooltipHover>
						)}
					</Stack>
				),
			},
		],
	}))
	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTop = 0
		}
	}, [page])

	return (
		<TableContainer
			ref={containerRef}
			sx={{
				backgroundColor: '#fff',
				marginTop: 0,
				overflowY: 'auto',
				// overflowX: 'auto',
				height: 'calc(100vh - 350px)',
				pr: '16px',
				'&::-webkit-scrollbar': {
					width: 4,
					height: 4,
				},
				'&::-webkit-scrollbar-track': {
					backgroundColor: '#DBDBDB',
					borderRadius: 12,
				},
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: COLORS.icon,
					borderRadius: 12,
				},
			}}
		>
			<Table
				sx={{
					tableLayout: 'fixed',
					width: '100%',
					borderCollapse: 'separate',
					borderSpacing: '0 8px',
				}}
			>
				<TableHead
					sx={{
						[`& .${tableCellClasses.root}`]: {
							backgroundColor: '#fff',
						},
					}}
				>
					<CustomTableRow
						rowKey={tableHead.rowKey}
						cellItems={tableHead.cellItems}
						rowBorderBottom={tableHead.rowBorderBottom}
						rowHeight={tableHead.height}
					/>
				</TableHead>

				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell
								colSpan={8}
								padding='none'
								sx={{
									borderBottom: 'none',
									height: 'calc(100vh - 350px)',
									p: 0,
								}}
							>
								<TableSkeleton
									widthForHeader='100%'
									heightForHeader='34px'
									widthForBody='100%'
									heightForBody='34px'
									rowCount={10}
									totalHeight='calc(100vh - 350px)'
								/>
							</TableCell>
						</TableRow>
					) : tableEmptyState === 'no-data' ||
					  tableEmptyState === 'no-deviations' ||
					  !deviationsResult?.length ? (
						<TableRow
							sx={{
								width: '100%',
								[`& .${tableCellClasses.root}`]: {
									borderBottom: 'none',
									height: '280px',
								},
							}}
						>
							<TableCell colSpan={8} align={'center'} padding={'none'}>
								{tableEmptyState === 'no-data' ? (
									<NoDataPlaceholder height='auto' />
								) : (
									<TypographySecondary14>
										Отклонений не обнаружено
									</TypographySecondary14>
								)}
							</TableCell>
						</TableRow>
					) : (
						<>
							{isRowNumberReversed
								? transformedData
										?.reverse()
										.map(row => <CustomTableRow key={row.rowKey} {...row} />)
								: transformedData?.map(row => (
										<CustomTableRow key={row.rowKey} {...row} />
									))}
						</>
					)}
				</TableBody>
			</Table>

			<DeviationVideoModal
				open={isVideoOpen}
				videoUrl={videoUrl || ''}
				entry={selectedEntry ?? undefined}
				canPrev={canPrev}
				canNext={canNext}
				onClose={closeModal}
				onPrev={showPrev}
				onNext={showNext}
				isLoading={isLoading}
				onToggleFalseMark={handleToggleFalseMark}
				isFalseMarkUpdating={isFalseMarkUpdating}
			/>
		</TableContainer>
	)
}
